import React, { useMemo } from "react";
import { Modal } from "antd";

const BillBreakdownOrder = ({ order, stock = [], open = false, onClose }) => {
  const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;
  const norm = (id) => (id ? id.toString().trim() : "");

  const stockMap = useMemo(() => {
    const map = {};
    stock.forEach((s) => {
      const raw =
        s.menuItemId && typeof s.menuItemId === "object"
          ? s.menuItemId._id
          : s.menuItemId;
      map[norm(raw)] = s.inStock;
    });
    return map;
  }, [stock]);

  if (!order) return null;

  const {
    cart = [],
    additionalCharges: orderCharges = [],
    taxes = [],
  } = order;

  const displayCharges =
    orderCharges && orderCharges.length
      ? orderCharges
      : [
          { label: "Thermocol Box", amount: 0 },
          { label: "Dry Ice", amount: 0 },
        ];

  const taxesArray = Array.isArray(taxes) ? taxes : [];

  const cgstTax = taxesArray.find((t) => t.gstType === "CGST") || { rate: 0 };
  const sgstTax = taxesArray.find((t) => t.gstType === "SGST") || { rate: 0 };
  const igstTax = taxesArray.find((t) => t.gstType === "IGST") || { rate: 0 };

  const cgstRate = Number(cgstTax.rate || 0);
  const sgstRate = Number(sgstTax.rate || 0);
  const igstRate = Number(igstTax.rate || 0);

  const totalBeforeTax = round2(
    cart.reduce((sum, item) => {
      const excRate = round2(
        item.price / (1 + (cgstRate + sgstRate + igstRate) / 100)
      );
      return sum + excRate * item.quantity;
    }, 0)
  );

  const cgst = round2((totalBeforeTax * cgstRate) / 100);
  const sgst = round2((totalBeforeTax * sgstRate) / 100);
  const igst = round2((totalBeforeTax * igstRate) / 100);

  const additionalTotal = round2(
    displayCharges.reduce((sum, ch) => sum + (Number(ch.amount) || 0), 0)
  );

  const grandTotal = Math.round(
    totalBeforeTax + cgst + sgst + igst + additionalTotal
  );

  return (
    <Modal
      title="Bill Breakdown"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 14,
          color: "black",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5", textAlign: "left" }}>
            <th style={{ border: "1px solid #000", padding: "8px" }}>Sr No</th>
            <th style={{ border: "1px solid #000", padding: "8px" }}>Item</th>
            <th style={{ border: "1px solid #000", padding: "8px" }}>Qty</th>
            <th style={{ border: "1px solid #000", padding: "8px" }}>
              Rate (Inc. Tax)
            </th>
            <th style={{ border: "1px solid #000", padding: "8px" }}>
              Rate (Exc. Tax)
            </th>
            <th style={{ border: "1px solid #000", padding: "8px" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {cart.map((item, idx) => {
            const qty = item.quantity;
            const incRate = item.price;
            const excRate = round2(
              incRate / (1 + (cgstRate + sgstRate + igstRate) / 100)
            );
            const amount = round2(qty * excRate);

            return (
              <tr key={item._id}>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "center",
                  }}
                >
                  {idx + 1}
                </td>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  {item.name}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "center",
                  }}
                >
                  {qty}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "right",
                  }}
                >
                  ₹{incRate.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "right",
                  }}
                >
                  ₹{excRate.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "right",
                  }}
                >
                  ₹{amount.toFixed(2)}
                </td>
              </tr>
            );
          })}

          {/* Totals */}
          <tr>
            <td
              colSpan="5"
              style={{
                textAlign: "right",
                padding: "8px",
                border: "1px solid #000",
              }}
            >
              <strong>Total Before Tax:</strong>
            </td>
            <td style={{ textAlign: "right", padding: "8px", border: "1px solid #000" }}>
              ₹{totalBeforeTax.toFixed(2)}
            </td>
          </tr>

          {cgstRate > 0 && (
  <tr>
    <td
      colSpan="5"
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      <strong>CGST ({cgstRate}%):</strong>
    </td>
    <td
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      ₹{cgst.toFixed(2)}
    </td>
  </tr>
)}
{sgstRate > 0 && (
  <tr>
    <td
      colSpan="5"
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      <strong>SGST ({sgstRate}%):</strong>
    </td>
    <td
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      ₹{sgst.toFixed(2)}
    </td>
  </tr>
)}
{igstRate > 0 && (
  <tr>
    <td
      colSpan="5"
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      <strong>IGST ({igstRate}%):</strong>
    </td>
    <td
      style={{
        textAlign: "right",
        padding: "8px",
        border: "1px solid #000",
      }}
    >
      ₹{igst.toFixed(2)}
    </td>
  </tr>
)}


          {displayCharges.map((charge, idx) => (
            <tr key={idx}>
              <td
                colSpan="5"
                style={{
                  textAlign: "right",
                  padding: "8px",
                  border: "1px solid #000",
                }}
              >
                <strong>{charge.label}:</strong>
              </td>
              <td style={{ textAlign: "right", padding: "8px", border: "1px solid #000" }}>
                ₹{Number(charge.amount).toFixed(2)}
              </td>
            </tr>
          ))}

          {/* Grand Total */}
          <tr>
            <td
              colSpan="5"
              style={{
                textAlign: "right",
                padding: "8px",
                border: "2px solid #000",
              }}
            >
              <strong>Grand Total:</strong>
            </td>
            <td
              style={{
                textAlign: "right",
                padding: "8px",
                fontWeight: "bold",
                border: "2px solid #000",
              }}
            >
              ₹{grandTotal}
            </td>
          </tr>
        </tbody>
      </table>
    </Modal>
  );
};

export default BillBreakdownOrder;
